import React from 'react';
import { FaStar, FaArchive, FaUndo } from 'react-icons/fa';
import type { ParameterPreferenceIdentity, ParameterStatus } from '@/hooks/useParameterPreferences';

interface ParameterActionsProps {
    preferenceIdentity: ParameterPreferenceIdentity;
    status: ParameterStatus;
    onTogglePin: (identity: ParameterPreferenceIdentity) => void;
    onToggleArchive: (identity: ParameterPreferenceIdentity) => void;
    disabled?: boolean;
}

const ParameterActions: React.FC<ParameterActionsProps> = ({
    preferenceIdentity,
    status,
    onTogglePin,
    onToggleArchive,
    disabled = false
}) => {
    const handlePinClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        onTogglePin(preferenceIdentity);
    };

    const handleArchiveClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        onToggleArchive(preferenceIdentity);
    };

    return (
        <div className="flex items-center gap-1">
            {/* Pin button */}
            <button
                aria-label={status === 'pinned' ? 'Unpin parameter' : 'Pin parameter'}
                className={`
                    p-1.5 rounded transition-all duration-200
                    ${
                        status === 'pinned'
                            ? 'text-yellow-400 hover:text-yellow-300 bg-yellow-400/10'
                            : 'text-zinc-500 hover:text-yellow-400 hover:bg-yellow-400/10'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                disabled={disabled}
                title={status === 'pinned' ? 'Unpin parameter' : 'Pin parameter'}
                onClick={handlePinClick}
            >
                <FaStar className={status === 'pinned' ? 'fill-current' : ''} size={14} />
            </button>

            {/* Archive/Restore button */}
            <button
                aria-label={status === 'archived' ? 'Restore parameter' : 'Archive parameter'}
                className={`
                    p-1.5 rounded transition-all duration-200
                    ${
                        status === 'archived'
                            ? 'text-blue-400 hover:text-blue-300 bg-blue-400/10'
                            : 'text-zinc-500 hover:text-red-400 hover:bg-red-400/10'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                disabled={disabled}
                title={status === 'archived' ? 'Restore parameter' : 'Archive parameter'}
                onClick={handleArchiveClick}
            >
                {status === 'archived' ? <FaUndo size={14} /> : <FaArchive size={14} />}
            </button>
        </div>
    );
};

export default ParameterActions;
